import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { EmailVerificationProvider, emailVerificationApi, EmailVerificationRequest, VerificationStatusResponse, ActiveVerificationResponse } from '../../api/emailVerification';

interface ActiveVerification {
  requestId: number;
  emailIds: number[];
  emailType: 'person' | 'place';
  total: number;
  processed: number;
  status: string;
  startTime: number;
  creditsHeld: number;
  verification_provider?: EmailVerificationProvider;
  searchId?: number;
  by_status?: {
    GUARANTEED?: number;
    RISKY?: number;
    INVALID?: number;
    PENDING?: number;
    INBOX_FULL?: number;
  };
}

interface EmailVerificationState {
  activeVerification: ActiveVerification | null;
  loading: boolean;
  error: string | null;
  completedVerification: VerificationStatusResponse | null;
}

const initialState: EmailVerificationState = {
  activeVerification: null,
  loading: false,
  error: null,
  completedVerification: null,
};

const formatError = (error: any): string => {
  if (typeof error === 'string') return error;
  if (error?.response?.data?.detail) {
    return typeof error.response.data.detail === 'string'
      ? error.response.data.detail
      : JSON.stringify(error.response.data.detail);
  }
  return 'An unexpected error occurred';
};

export const startEmailVerification = createAsyncThunk(
  'emailVerification/start',
  async (
    params: EmailVerificationRequest,
    { rejectWithValue }
  ) => {
    try {
      const response = await emailVerificationApi.verify(params);
      return {
        ...response,
        emailIds: params.email_ids || [],
        emailType: params.email_type,
        searchId: params.search_id,
      };
    } catch (error: any) {
      if (error?.response?.status === 402) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(formatError(error));
    }
  }
);

export const pollVerificationStatus = createAsyncThunk(
  'emailVerification/pollStatus',
  async (requestId: number, { rejectWithValue }) => {
    try {
      return await emailVerificationApi.getStatus(requestId);
    } catch (error: any) {
      return rejectWithValue(formatError(error));
    }
  }
);

export const checkActiveVerification = createAsyncThunk(
  'emailVerification/checkActive',
  async (_, { rejectWithValue }) => {
    try {
      return await emailVerificationApi.getActive();
    } catch (error: any) {
      return rejectWithValue(formatError(error));
    }
  }
);

const emailVerificationSlice = createSlice({
  name: 'emailVerification',
  initialState,
  reducers: {
    clearVerification: (state) => {
      state.activeVerification = null;
      state.completedVerification = null;
      state.error = null;
    },
    clearCompletedVerification: (state) => {
      state.completedVerification = null;
    },
    setCompletedVerification: (state, action: PayloadAction<VerificationStatusResponse>) => {
      state.completedVerification = action.payload;
    },
    setActiveVerification: (state, action: PayloadAction<ActiveVerification>) => {
      state.activeVerification = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startEmailVerification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startEmailVerification.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        
        if (payload.processing_mode === 'sync') {
          state.activeVerification = null;
        } else {
          state.activeVerification = {
            requestId: payload.request_id,
            emailIds: payload.emailIds,
            emailType: payload.emailType,
            total: payload.emails_processing || payload.emailIds.length,
            processed: 0,
            status: 'processing',
            startTime: Date.now(),
            creditsHeld: payload.credits_held || 0,
            searchId: payload.searchId,
          };
        }
      })
      .addCase(startEmailVerification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(pollVerificationStatus.fulfilled, (state, action) => {
        if (state.activeVerification) {
          state.activeVerification.processed = action.payload.processed ?? action.payload.results.length;
          state.activeVerification.total = action.payload.total ?? state.activeVerification.total;
          state.activeVerification.status = action.payload.status;
          if (action.payload.by_status) {
            state.activeVerification.by_status = action.payload.by_status;
          }
          state.activeVerification.verification_provider = action.payload.verification_provider;

          if (
            (action.payload.status === 'completed' || action.payload.status === 'failed') &&
            !state.completedVerification
          ) {
            state.completedVerification = action.payload;
          }
        }
      })
      .addCase(pollVerificationStatus.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(checkActiveVerification.fulfilled, (state, action) => {
        const payload = action.payload as ActiveVerificationResponse;
        if (payload.has_active && payload.request_id) {
          state.activeVerification = {
            requestId: payload.request_id,
            emailIds: [],
            emailType: 'person',
            total: payload.total || 0,
            processed: payload.processed || 0,
            status: payload.status || 'processing',
            startTime: payload.started_at ? new Date(payload.started_at).getTime() : Date.now(),
            creditsHeld: payload.credits_held || 0,
            verification_provider: payload.verification_provider,
            searchId: payload.search_id,
          };
        }
      });
  },
});

export const { clearVerification, clearCompletedVerification, setActiveVerification, setCompletedVerification } = emailVerificationSlice.actions;

export default emailVerificationSlice.reducer;
